/**
 * 事务相关常量定义
 */
import { ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, StopOutlined, QuestionCircleOutlined, SendOutlined, FileTextOutlined } from "@ant-design/icons";

// 事务类型
export const TRANSACTION_TYPES = {
    PERMIT_REQUEST: "PERMIT_REQUEST",
    FUEL_REQUEST: "FUEL_REQUEST",
    FLIGHT_BRIEF: "FLIGHT_BRIEF",
    CATERING_ORDER: "CATERING_ORDER",
    CREW_TRANSPORTATION: "CREW_TRANSPORTATION",
    HOTAC: "HOTAC",
};

// 事务分类定义 - 用于组织和筛选事务
export const TRANSACTION_CATEGORIES = {
    PLAN_CHANGES: {
        key: "PLAN_CHANGES",
        label: "计划变更",
        description: "航班时刻调整、状态变更、航线变化等计划相关变更",
        color: "#1890ff",
        priority: 1,
        // 这个分类主要对应航班历史中的计划变更事件，不直接对应具体的事务类型
        transactionTypes: [], // 计划变更不对应具体的事务类型
    },
    FLIGHT_INFO: {
        key: "FLIGHT_INFO",
        label: "航班信息",
        description: "MVT航班信息变更、航班属性更新等",
        color: "#52c41a",
        priority: 2,
        // 这个分类主要对应航班信息相关的事件
        transactionTypes: [], // 航班信息变更不对应具体的事务类型
    },
    PERMITS: {
        key: "PERMITS",
        label: "飞行许可",
        description: "UAS、民航局、香港民航处等飞行许可申请",
        color: "#fa8c16",
        priority: 3,
        transactionTypes: [TRANSACTION_TYPES.PERMIT_REQUEST],
    },
    FUEL: {
        key: "FUEL",
        label: "燃油保障",
        description: "AEG、Chevron、AFS、EJET等燃油供应商服务",
        color: "#eb2f96",
        priority: 4,
        transactionTypes: [TRANSACTION_TYPES.FUEL_REQUEST],
    },
    CATERING: {
        key: "CATERING",
        label: "航食配餐",
        description: "各机场航食供应商配餐服务",
        color: "#722ed1",
        priority: 5,
        transactionTypes: [TRANSACTION_TYPES.CATERING_ORDER],
    },
    CREW_SERVICES: {
        key: "CREW_SERVICES",
        label: "机组服务",
        description: "地面交通、酒店住宿等机组服务",
        color: "#13c2c2",
        priority: 6,
        transactionTypes: [TRANSACTION_TYPES.CREW_TRANSPORTATION, TRANSACTION_TYPES.HOTAC],
    },
    FLIGHT_BRIEF: {
        key: "FLIGHT_BRIEF",
        label: "飞行简报",
        description: "飞行简报生成和发送",
        color: "#f759ab",
        priority: 7,
        transactionTypes: [TRANSACTION_TYPES.FLIGHT_BRIEF],
    },
    OTHER: {
        key: "OTHER",
        label: "其他",
        description: "未分类的其他事务类型",
        color: "#8c8c8c",
        priority: 99,
        transactionTypes: [], // 其他类别用于兜底，包含所有未明确分类的事务
    },
};

// 事务类型对应的表名
export const TRANSACTION_TABLE_MAP = {
    [TRANSACTION_TYPES.PERMIT_REQUEST]: "permit_request",
    [TRANSACTION_TYPES.FUEL_REQUEST]: "fuel_request",
    [TRANSACTION_TYPES.CATERING_ORDER]: "catering_order",
    [TRANSACTION_TYPES.FLIGHT_BRIEF]: "flight_brief",
    [TRANSACTION_TYPES.OCC_NOTE]: "occ_note",
    [TRANSACTION_TYPES.CREW_TRANSPORTATION]: "crew_transportation",
    [TRANSACTION_TYPES.HOTAC]: "hotac",
};

// 事务类型显示名称
export const TRANSACTION_TYPE_LABELS = {
    [TRANSACTION_TYPES.PERMIT_REQUEST]: "许可",
    [TRANSACTION_TYPES.FUEL_REQUEST]: "燃油",
    [TRANSACTION_TYPES.CATERING_ORDER]: "配餐",
    [TRANSACTION_TYPES.FLIGHT_BRIEF]: "简报",
    // [TRANSACTION_TYPES.OCC_NOTE]: "OCC备注",
    [TRANSACTION_TYPES.CREW_TRANSPORTATION]: "机组接送",
    [TRANSACTION_TYPES.HOTAC]: "酒店住宿",
};

// 通用事务状态
export const TRANSACTION_STATUS = {
    PENDING: "PENDING",
    APPROVED: "APPROVED",
    REJECTED: "REJECTED",
    CANCELLED: "CANCELLED",
};

// 事务类型特定状态
export const TRANSACTION_TYPE_SPECIFIC_STATUS = {
    [TRANSACTION_TYPES.PERMIT_REQUEST]: {
        PENDING: "PENDING",
        APPROVED: "APPROVED",
        REJECTED: "REJECTED",
        CANCELLED: "CANCELLED",
    },
    [TRANSACTION_TYPES.FUEL_REQUEST]: {
        PENDING: "PENDING",
        APPROVED: "APPROVED",
        REJECTED: "REJECTED",
        CANCELLED: "CANCELLED",
    },
    [TRANSACTION_TYPES.CATERING_ORDER]: {
        PENDING: "PENDING",
        APPROVED: "APPROVED",
        REJECTED: "REJECTED",
        CANCELLED: "CANCELLED",
    },
    [TRANSACTION_TYPES.FLIGHT_BRIEF]: {
        PENDING: "PENDING",
        COMPLETED: "COMPLETED",
        SENT: "SENT",
        CANCELLED: "CANCELLED",
    },
    [TRANSACTION_TYPES.OCC_NOTE]: {
        PENDING: "PENDING",
        APPROVED: "APPROVED",
        REJECTED: "REJECTED",
        CANCELLED: "CANCELLED",
    },
    [TRANSACTION_TYPES.CREW_TRANSPORTATION]: {
        PENDING: "PENDING",
        BOOKED: "BOOKED",
        CONFIRMED: "CONFIRMED",
        REJECTED: "REJECTED",
        CANCELLED: "CANCELLED",
    },
    [TRANSACTION_TYPES.HOTAC]: {
        PENDING: "PENDING",
        BOOKED: "BOOKED",
        CONFIRMED: "CONFIRMED",
        REJECTED: "REJECTED",
        CANCELLED: "CANCELLED",
    },
};

// 事务状态显示文本
export const TRANSACTION_STATUS_LABELS = {
    [TRANSACTION_STATUS.PENDING]: "Pending",
    [TRANSACTION_STATUS.APPROVED]: "Approved",
    [TRANSACTION_STATUS.REJECTED]: "Rejected",
    [TRANSACTION_STATUS.CANCELLED]: "Cancelled",
    // 添加特定事务类型状态的标签
    PROCESSING: "Processing",
    DELIVERED: "Delivered",
    DELAYED: "Delayed",
    ORDERED: "Ordered",
    COMPLETED: "Completed",
    BOOKED: "Booked",
    CONFIRMED: "Confirmed",
    REJECTED: "Rejected",
    SENT: "Sent",
};

// 事务状态图标
export const TRANSACTION_STATUS_ICONS = {
    [TRANSACTION_STATUS.PENDING]: <ClockCircleOutlined />,
    [TRANSACTION_STATUS.APPROVED]: <CheckCircleOutlined />,
    [TRANSACTION_STATUS.REJECTED]: <CloseCircleOutlined />,
    [TRANSACTION_STATUS.CANCELLED]: <StopOutlined />,
    PROCESSING: <ClockCircleOutlined />,
    DELIVERED: <CheckCircleOutlined />,
    DELAYED: <ClockCircleOutlined />,
    ORDERED: <ClockCircleOutlined />,
    COMPLETED: <FileTextOutlined />,
    SENT: <SendOutlined />,
    BOOKED: <ClockCircleOutlined />,
    CONFIRMED: <CheckCircleOutlined />,
    REJECTED: <CloseCircleOutlined />,
    default: <QuestionCircleOutlined />,
};

// 事务状态颜色
export const TRANSACTION_STATUS_COLORS = {
    [TRANSACTION_STATUS.PENDING]: "#f5222d",
    [TRANSACTION_STATUS.APPROVED]: "#52c41a",
    [TRANSACTION_STATUS.REJECTED]: "#f5222d",
    [TRANSACTION_STATUS.CANCELLED]: "#ffb84d",
    PROCESSING: "#1890ff",
    DELIVERED: "#52c41a",
    DELAYED: "#faad14",
    ORDERED: "#1890ff",
    COMPLETED: "#1890ff",
    SENT: "#52c41a",
    BOOKED: "#1890ff",
    CONFIRMED: "#52c41a",
    REJECTED: "#f5222d",
    default: "#d9d9d9",
};

// 事务API路径映射
export const TRANSACTION_API_ENDPOINTS = {
    [TRANSACTION_TYPES.FUEL_REQUEST]: "fuel-requests",
    [TRANSACTION_TYPES.PERMIT_REQUEST]: "permits",
    [TRANSACTION_TYPES.CATERING_ORDER]: "catering-orders",
    [TRANSACTION_TYPES.FLIGHT_BRIEF]: "flight-briefs",
    [TRANSACTION_TYPES.CREW_TRANSPORTATION]: "crew-transportations",
    [TRANSACTION_TYPES.HOTAC]: "hotacs",
};

// 事务变更类型映射
export const TRANSACTION_CHANGE_TYPES = {
    [TRANSACTION_TYPES.FUEL_REQUEST]: {
        create: "INITIATE_FUEL_REQUEST",
        update: "UPDATE_FUEL_STATUS",
    },
    [TRANSACTION_TYPES.PERMIT_REQUEST]: {
        create: "INITIATE_PERMIT_REQUEST",
        update: "UPDATE_PERMIT_STATUS",
    },
    [TRANSACTION_TYPES.CATERING_ORDER]: {
        create: "INITIATE_CATERING_ORDER",
        update: "UPDATE_CATERING_STATUS",
    },
    [TRANSACTION_TYPES.FLIGHT_BRIEF]: {
        create: "GENERATE_FLIGHT_BRIEF",
        update: "UPDATE_FLIGHT_BRIEF_STATUS",
    },
    [TRANSACTION_TYPES.OCC_NOTE]: {
        create: "CREATE_OCC_NOTE",
        update: "UPDATE_OCC_NOTE",
    },
    [TRANSACTION_TYPES.CREW_TRANSPORTATION]: {
        create: "INITIATE_CREW_TRANSPORTATION",
        update: "UPDATE_CREW_TRANSPORTATION_STATUS",
    },
    [TRANSACTION_TYPES.HOTAC]: {
        create: "INITIATE_HOTAC",
        update: "UPDATE_HOTAC_STATUS",
    },
};

// 不同事务类型的默认详情字段结构
export const DEFAULT_TRANSACTION_DETAILS = {
    [TRANSACTION_TYPES.FUEL_REQUEST]: {
        orderNumber: "",
        expiryDate: null,
        maxPayload: null,
    },
    [TRANSACTION_TYPES.PERMIT_REQUEST]: {
        departureLandingPermission: {
            status: "PENDING",
            referenceNumber: "",
            manifestNumber: "",
            remarks: "",
        },
        arrivalLandingPermission: {
            status: "PENDING",
            referenceNumber: "",
            manifestNumber: "",
            remarks: "",
        },
        overFlightPermission: {
            status: "PENDING",
            referenceNumber: "",
            std: null,
            sta: null,
            delayHours: null,
            remarks: "",
        },
    },
    [TRANSACTION_TYPES.CATERING_ORDER]: {
        version: "V1",
        crewNumber: 2,
        orderNo: "",
    },
    [TRANSACTION_TYPES.FLIGHT_BRIEF]: {
        maxPayload: null,
        version: "V1",
    },
    [TRANSACTION_TYPES.OCC_NOTE]: null,
    [TRANSACTION_TYPES.CREW_TRANSPORTATION]: {
        route: "Hotel - Airport",
        pickup_time: null,
        pax_number: 2,
        version: "V1",
        order_number: "",
    },
    [TRANSACTION_TYPES.HOTAC]: {
        pax_number: 2,
        version: "V1",
        order_number: "",
    },
};

// 前端事务类型常量 到 后端模型名称 的映射
export const TRANSACTION_TYPE_TO_MODEL_MAP = {
    [TRANSACTION_TYPES.FUEL_REQUEST]: "FuelRequest",
    [TRANSACTION_TYPES.PERMIT_REQUEST]: "PermitRequest",
    [TRANSACTION_TYPES.CATERING_ORDER]: "CateringOrder",
    [TRANSACTION_TYPES.FLIGHT_BRIEF]: "FlightBrief",
    [TRANSACTION_TYPES.CREW_TRANSPORTATION]: "CrewTransportation",
    [TRANSACTION_TYPES.HOTAC]: "Hotac",
};

// 后端模型名称 到 前端事务类型常量 的映射
export const MODEL_TO_TRANSACTION_TYPE_MAP = {
    FuelRequest: TRANSACTION_TYPES.FUEL_REQUEST,
    PermitRequest: TRANSACTION_TYPES.PERMIT_REQUEST,
    CateringOrder: TRANSACTION_TYPES.CATERING_ORDER,
    FlightBrief: TRANSACTION_TYPES.FLIGHT_BRIEF,
    CrewTransportation: TRANSACTION_TYPES.CREW_TRANSPORTATION,
    Hotac: TRANSACTION_TYPES.HOTAC,
};

// 事务迁移状态
export const TRANSACTION_MIGRATION_STATUS = {
    PENDING_REVIEW: "PENDING_REVIEW",
    CONFIRMED: "CONFIRMED",
    REJECTED: "REJECTED",
};

// 事务迁移状态标签
export const TRANSACTION_MIGRATION_STATUS_LABELS = {
    [TRANSACTION_MIGRATION_STATUS.PENDING_REVIEW]: "待检",
    [TRANSACTION_MIGRATION_STATUS.CONFIRMED]: "已确认",
    [TRANSACTION_MIGRATION_STATUS.REJECTED]: "已拒绝",
};

// 事务迁移状态颜色
export const TRANSACTION_MIGRATION_STATUS_COLORS = {
    [TRANSACTION_MIGRATION_STATUS.PENDING_REVIEW]: "#faad14",
    [TRANSACTION_MIGRATION_STATUS.CONFIRMED]: "#52c41a",
    [TRANSACTION_MIGRATION_STATUS.REJECTED]: "#ff4d4f",
};

// 事务筛选相关常量
export const TRANSACTION_FILTER_OPTIONS = {
    UNSET: "UNSET",
    // 添加迁移状态筛选选项
    ...TRANSACTION_MIGRATION_STATUS,
};

// 事务筛选状态标签
export const TRANSACTION_FILTER_STATUS_LABELS = {
    [TRANSACTION_FILTER_OPTIONS.UNSET]: "未发起/未设置",
    ...TRANSACTION_STATUS_LABELS,
    ...TRANSACTION_MIGRATION_STATUS_LABELS,
};

// 事务筛选状态颜色
export const TRANSACTION_FILTER_STATUS_COLORS = {
    [TRANSACTION_FILTER_OPTIONS.UNSET]: "#999",
    ...TRANSACTION_STATUS_COLORS,
    ...TRANSACTION_MIGRATION_STATUS_COLORS,
};

// 事务筛选状态图标
export const TRANSACTION_FILTER_STATUS_ICONS = {
    [TRANSACTION_FILTER_OPTIONS.UNSET]: <QuestionCircleOutlined />,
    ...TRANSACTION_STATUS_ICONS,
};

// 默认事务筛选状态（空表示不筛选）
export const DEFAULT_TRANSACTION_FILTERS = {
    [TRANSACTION_TYPES.FUEL_REQUEST]: [],
    [TRANSACTION_TYPES.PERMIT_REQUEST]: [],
    [TRANSACTION_TYPES.CATERING_ORDER]: [],
    [TRANSACTION_TYPES.FLIGHT_BRIEF]: [],
    [TRANSACTION_TYPES.CREW_TRANSPORTATION]: [],
    [TRANSACTION_TYPES.HOTAC]: [],
};

// 辅助函数：根据事务类型获取对应的分类
export const getTransactionCategory = (transactionType) => {
    for (const [categoryKey, category] of Object.entries(TRANSACTION_CATEGORIES)) {
        if (category.transactionTypes.includes(transactionType)) {
            return categoryKey;
        }
    }
    return TRANSACTION_CATEGORIES.OTHER.key;
};

// 辅助函数：根据事务类型获取对应的航班历史分类
export const getFlightHistoryCategoryForTransaction = (transactionType) => {
    const transactionCategory = getTransactionCategory(transactionType);
    return TRANSACTION_TO_FLIGHT_HISTORY_CATEGORY_MAP[transactionCategory] || "other";
};

// 辅助函数：获取事务类型对应的允许航班历史分类
// 每种事务类型应该看到自己的事务类型 + 航班变更 + 邮件通知
export const getAllowedFlightHistoryCategories = (transactionType) => {
    const transactionHistoryCategory = getFlightHistoryCategoryForTransaction(transactionType);

    // 每种事务类型应该看到：自己的事务类型 + 计划变更 + 其他（包含邮件通知）
    // 注意：分类名称必须与 FlightHistoryTimeline 组件中的 BUSINESS_CATEGORIES 保持一致
    const allowedCategories = new Set([transactionHistoryCategory, "plan_changes", "other"]);

    return Array.from(allowedCategories);
};

// 辅助函数：根据分类获取包含的事务类型
export const getTransactionTypesByCategory = (categoryKey) => {
    const category = TRANSACTION_CATEGORIES[categoryKey];
    return category ? category.transactionTypes : [];
};

// 辅助函数：获取所有分类的列表（按优先级排序）
export const getAllTransactionCategories = () => {
    return Object.values(TRANSACTION_CATEGORIES).sort((a, b) => a.priority - b.priority);
};

// 事务分类到航班历史分类的映射关系
// 注意：这里的分类名称必须与 FlightHistoryTimeline 组件中的 BUSINESS_CATEGORIES 保持一致
export const TRANSACTION_TO_FLIGHT_HISTORY_CATEGORY_MAP = {
    [TRANSACTION_CATEGORIES.PLAN_CHANGES.key]: "plan_changes", // 计划变更 -> 计划变更
    [TRANSACTION_CATEGORIES.FLIGHT_INFO.key]: "flight_info", // 航班信息 -> 航班信息
    [TRANSACTION_CATEGORIES.PERMITS.key]: "permits", // 飞行许可 -> 飞行许可
    [TRANSACTION_CATEGORIES.FUEL.key]: "fuel", // 燃油保障 -> 燃油保障
    [TRANSACTION_CATEGORIES.CATERING.key]: "catering", // 航食配餐 -> 航食配餐
    [TRANSACTION_CATEGORIES.CREW_SERVICES.key]: "crew_services", // 机组服务 -> 机组服务
    [TRANSACTION_CATEGORIES.FLIGHT_BRIEF.key]: "flight_brief", // 飞行简报 -> 飞行简报
    [TRANSACTION_CATEGORIES.OTHER.key]: "other", // 其他 -> 其他（包含邮件通知）
};

// 许可类型常量 - 根据业务需求重新定义
export const PERMIT_TYPES = {
    OVERFLIGHT: "OVERFLIGHT", // 飞越许可
    LANDING_TAKEOFF: "LANDING_TAKEOFF", // 起降许可（包含起飞和落地）
    // 保留旧的类型以兼容现有数据
    DEPARTURE: "DEPARTURE", // 起飞（将映射到LANDING_TAKEOFF）
    ARRIVAL: "ARRIVAL", // 落地（将映射到LANDING_TAKEOFF）
};

// 许可类型显示配置
export const PERMIT_TYPE_CONFIG = {
    [PERMIT_TYPES.OVERFLIGHT]: { color: "blue", text: "飞越许可" },
    [PERMIT_TYPES.LANDING_TAKEOFF]: { color: "green", text: "起降许可" },
    // 兼容旧类型
    [PERMIT_TYPES.DEPARTURE]: { color: "green", text: "起飞许可" },
    [PERMIT_TYPES.ARRIVAL]: { color: "orange", text: "落地许可" },
};

// 许可类型映射 - 将旧类型映射到新类型
export const PERMIT_TYPE_MAPPING = {
    [PERMIT_TYPES.DEPARTURE]: PERMIT_TYPES.LANDING_TAKEOFF,
    [PERMIT_TYPES.ARRIVAL]: PERMIT_TYPES.LANDING_TAKEOFF,
    [PERMIT_TYPES.OVERFLIGHT]: PERMIT_TYPES.OVERFLIGHT,
    [PERMIT_TYPES.LANDING_TAKEOFF]: PERMIT_TYPES.LANDING_TAKEOFF,
};

// 合同项标签常量 - 用于标记合同项的许可类型
export const CONTRACT_PERMIT_TAGS = {
    OVERFLIGHT: "OVERFLIGHT", // 飞越许可标签
    LANDING_TAKEOFF: "LANDING_TAKEOFF", // 起降许可标签
    // 保留旧标签以兼容
    DEPARTURE: "DEPARTURE",
    ARRIVAL: "ARRIVAL",
};

// 事务明细数据来源类型
export const DETAIL_SOURCE = {
    API: "API", // 从API加载的真实数据
    GENERATED: "GENERATED", // 根据合同项自动生成的数据
    USER_CREATED: "USER_CREATED", // 用户手动创建的数据
};

// 状态选择器选项（用于明细表格）
export const STATUS_SELECTOR_OPTIONS = [
    { value: "", label: "-", color: "default" },
    { value: "PENDING", label: "待处理", color: "orange" },
    { value: "APPROVED", label: "已批准", color: "green" },
    { value: "REJECTED", label: "已拒绝", color: "red" },
    { value: "CANCELLED", label: "已取消", color: "default" },
];

// 事务状态配置（用于TransactionHeader等组件）
export const TRANSACTION_STATUS_CONFIG = {
    PENDING: { color: "orange", icon: "ClockCircleOutlined" },
    APPROVED: { color: "green", icon: "CheckCircleOutlined" },
    REJECTED: { color: "red", icon: "ExclamationCircleOutlined" },
    CANCELLED: { color: "default", icon: "ExclamationCircleOutlined" },
};

/**
 * 获取指定事务类型的状态选项（用于状态编辑器）
 * @param {string} transactionType - 事务类型
 * @returns {Array} 状态选项数组，格式：[{value, label, color}]
 */
export const getTransactionStatusOptions = (transactionType) => {
    const statusKeys = Object.keys(TRANSACTION_TYPE_SPECIFIC_STATUS[transactionType] || {});

    return statusKeys.map((statusKey) => ({
        value: statusKey,
        label: TRANSACTION_STATUS_LABELS[statusKey] || statusKey,
        color: getStatusColorName(TRANSACTION_STATUS_COLORS[statusKey] || TRANSACTION_STATUS_COLORS.default),
    }));
};

/**
 * 将颜色代码转换为 Ant Design 的颜色名称
 * @param {string} colorCode - 颜色代码
 * @returns {string} Ant Design 颜色名称
 */
const getStatusColorName = (colorCode) => {
    const colorMap = {
        "#faad14": "orange",
        "#52c41a": "green",
        "#f5222d": "red",
        "#d9d9d9": "default",
        "#1890ff": "blue",
        "#13c2c2": "cyan",
    };
    return colorMap[colorCode] || "default";
};
